/**
 * Better Auth's three endpoints, spoken over plain `fetch`.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY NOT THE `better-auth/react` CLIENT
 * ─────────────────────────────────────────────────────────────────────────
 * Two reasons, one practical and one structural.
 *
 * Practical: `better-auth` is a dependency of `apps/api` only. It is not
 * resolvable from `apps/web`, and this task was explicitly barred from running
 * `pnpm install` because another agent is working in `apps/api/` concurrently.
 * Adding it to package.json without installing would break typecheck and
 * build, which are hard gates.
 *
 * Structural, and the reason this is not merely a workaround: the one thing
 * the official client gives you is `authClient.useSession()`, and that hook is
 * exactly what this app cannot use. It fetches `/get-session` and reports
 * `data: null` whenever the fetch does not produce a session — with no way to
 * ask *why*. A dead booth wifi and a revoked device are indistinguishable
 * through it, and they must lead to opposite behaviour here. rcrcrc's
 * `react-ui/src/lib/auth-client.ts` papers over this with a five-minute
 * localStorage TTL behind `useSession`, which keeps a laptop usable across a
 * blip but still collapses to "logged out" after five minutes offline.
 *
 * Everything else the client offers (cookie handling, JSON bodies) is one line
 * of `fetch` each. So the surface below is three functions, and the classifier
 * in ../api/http.ts supplies the distinction the official hook cannot.
 *
 * If `better-auth` is ever added to apps/web for the admin plugin's client
 * methods, this module should keep owning session reads regardless.
 *
 * Endpoint paths, request bodies, response bodies and status codes below were
 * verified against the installed better-auth 1.6.25 source
 * (`dist/api/routes/sign-in.mjs`, `session.mjs`, `sign-out.mjs`,
 * `rate-limiter/index.mjs`), cross-checked with the context7 docs.
 */
import { apiRequest, unauthenticatedFromNullBody, type ApiResult } from '@/lib/api/http';

const AUTH_BASE = '/api/auth';

/**
 * The subset of Better Auth's user object this app reads.
 *
 * `role` comes from the admin plugin and is absent when that plugin is off, so
 * it is optional here rather than assumed — apps/api is being built
 * concurrently and this must not hard-fail if the plugin lands later.
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role?: string | null;
}

export interface AuthSession {
  id: string;
  userId: string;
  /** ISO 8601. Better Auth serialises its `Date` this way over the wire. */
  expiresAt: string;
}

export interface SessionPayload {
  user: AuthUser;
  session: AuthSession;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseUser(value: unknown): AuthUser | undefined {
  if (!isRecord(value)) return undefined;
  const { id, email, name, role } = value;
  if (typeof id !== 'string' || typeof email !== 'string') return undefined;
  return {
    id,
    email,
    name: typeof name === 'string' ? name : email,
    role: typeof role === 'string' ? role : null
  };
}

function parseSession(value: unknown): AuthSession | undefined {
  if (!isRecord(value)) return undefined;
  const { id, userId, expiresAt } = value;
  if (typeof id !== 'string' || typeof userId !== 'string') return undefined;
  // expiresAt may arrive as an ISO string (JSON) — anything else and we have
  // no horizon to cache against, so the payload is unusable.
  if (typeof expiresAt !== 'string') return undefined;
  return { id, userId, expiresAt };
}

function parseSessionPayload(value: unknown): SessionPayload | undefined {
  if (!isRecord(value)) return undefined;
  const user = parseUser(value['user']);
  const session = parseSession(value['session']);
  if (!user || !session) return undefined;
  return { user, session };
}

/**
 * `GET /api/auth/get-session`.
 *
 * Note the `unauthenticatedFromNullBody` wrap: this endpoint answers 200 with
 * a JSON `null` body — not a 401 — when the cookie is missing, expired or
 * revoked. Treating that as an ordinary empty success is how a revoked device
 * would keep its cached session forever. See the note on that helper.
 *
 * A malformed-but-2xx body is reported as an INTERNAL_ERROR rather than as
 * unauthenticated: we cannot tell what happened, and guessing "logged out"
 * here would be the strand-them-at-a-login-screen failure again.
 */
export async function fetchSession(options: { timeoutMs?: number } = {}): Promise<ApiResult<SessionPayload>> {
  const raw = await apiRequest<SessionPayload | null>(`${AUTH_BASE}/get-session`, {
    method: 'GET',
    timeoutMs: options.timeoutMs
  });
  const result = unauthenticatedFromNullBody(raw);
  if (result.kind !== 'ok') return result;

  const payload = parseSessionPayload(result.data);
  if (!payload) {
    return {
      kind: 'error',
      code: 'INTERNAL_ERROR',
      message: 'The server returned a session in an unexpected shape.',
      status: 200
    };
  }
  return { kind: 'ok', data: payload };
}

/**
 * `POST /api/auth/sign-in/email`.
 *
 * Its response is `{ redirect, token, url, user }` — deliberately note the
 * absence of a `session` object, and therefore of `expiresAt`. The caller must
 * follow a successful sign-in with `fetchSession()` to learn the horizon the
 * offline cache is measured against; there is no way to derive it from this
 * response. (Verified in better-auth 1.6.25 `dist/api/routes/sign-in.mjs`.)
 *
 * A wrong email and a wrong password both come back as 401
 * INVALID_EMAIL_OR_PASSWORD — Better Auth even hashes the supplied password
 * for a non-existent user to keep the timing similar. The login screen must
 * not undo that by distinguishing them in its copy.
 */
export async function signInWithEmail(input: {
  email: string;
  password: string;
}): Promise<ApiResult<{ user: AuthUser }>> {
  const result = await apiRequest<unknown>(`${AUTH_BASE}/sign-in/email`, {
    method: 'POST',
    body: { email: input.email, password: input.password, rememberMe: true }
  });
  if (result.kind !== 'ok') return result;

  const user = isRecord(result.data) ? parseUser(result.data['user']) : undefined;
  if (!user) {
    return {
      kind: 'error',
      code: 'INTERNAL_ERROR',
      message: 'Signed in, but the server returned an unexpected response.',
      status: 200
    };
  }
  return { kind: 'ok', data: { user } };
}

/** `POST /api/auth/sign-out`. Clears the server session and expires the cookie. */
export async function signOutOnServer(): Promise<ApiResult<unknown>> {
  return apiRequest<unknown>(`${AUTH_BASE}/sign-out`, { method: 'POST' });
}
